import React, { useCallback, useEffect, useMemo } from 'react';
import { scroller } from 'react-scroll';
import { debounce } from 'lodash';
import { faAngleDoubleLeft } from '@fortawesome/free-solid-svg-icons/faAngleDoubleLeft';
import { faAngleDoubleRight } from '@fortawesome/free-solid-svg-icons/faAngleDoubleRight';
import { faAngleLeft } from '@fortawesome/free-solid-svg-icons/faAngleLeft';
import { faAngleRight } from '@fortawesome/free-solid-svg-icons/faAngleRight';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useForm } from 'react-hook-form';

import TradesTable from 'buyside/components/presentation/InsiderTradesPage/TradesTable/tradesTable';

import { useInsiderTradesLazyQuery, InsiderTradesQueryVariables } from 'interfaces/graphql-typings';

import styles from './insiderTradesPageStyles.scss';
import paginationStyles from 'buyside/components/presentation/IndexPaginationNav/indexPaginationNavStyles.scss';

const tableName = 'recent-trades';

const RecentTrades: React.FC = () => {
  const { register } = useForm({
    defaultValues: { query: '', watchlistedTickers: false }
  });

  const [handleLoadTrades, { data, error, loading, variables }] = useInsiderTradesLazyQuery();

  if (error) throw error;

  const page = variables?.page || 1;
  const { trades, maxPage, resultCount } = data?.insiderTrades || {};

  const table = document.getElementById(`${tableName}-table`);
  const updateTable = useCallback(
    (variables: InsiderTradesQueryVariables, skipScroll?: boolean): void => {
      if (!skipScroll && table) {
        scroller.scrollTo(`${tableName}-table`, {
          duration: 0,
          offset: -70
        });
        table.scrollTo(0, 0);
      }
      handleLoadTrades({ variables });
    },
    [handleLoadTrades, table]
  );

  const debounceUpdateTable = useMemo(() => debounce(values => updateTable(values), 500), [
    updateTable
  ]);

  useEffect(() => {
    updateTable({ page: 1, watchlistedTickers: false, query: '' }, true);
  }, [updateTable]);

  return (
    <div id="recent-trades">
      <div className={styles.recentTradesHeader}>
        <h3>Recent Insider Trades</h3>
        <div className={styles.tableFilters}>
          <label>
            <input
              {...register('watchlistedTickers')}
              type="checkbox"
              onChange={e =>
                updateTable({ ...variables, page: 1, watchlistedTickers: e.target.checked })
              }
            />
            Only Show Watchlisted Tickers
          </label>
          <div>
            <input
              {...register('query')}
              placeholder="Filter by ticker"
              onChange={e => debounceUpdateTable({ ...variables, page: 1, query: e.target.value })}
            />
          </div>
        </div>
      </div>
      {(loading || data) && (
        <TradesTable loading={loading} tableName={tableName} trades={trades || []} />
      )}
      <div className={styles.recentTradesFooter}>
        <div>
          <button
            name="first"
            className={paginationStyles.navButton}
            disabled={loading || page === 1}
            onClick={(): void => updateTable({ ...variables, page: 1 })}
          >
            <FontAwesomeIcon icon={faAngleDoubleLeft} />
          </button>
          <button
            name="previous"
            className={paginationStyles.navButton}
            disabled={loading || page === 1}
            onClick={(): void => updateTable({ ...variables, page: page - 1 })}
          >
            <FontAwesomeIcon icon={faAngleLeft} />
          </button>
          <button
            name="next"
            className={paginationStyles.navButton}
            disabled={loading || page === maxPage}
            onClick={(): void => updateTable({ ...variables, page: page + 1 })}
          >
            <FontAwesomeIcon icon={faAngleRight} />
          </button>
          <button
            name="last"
            className={paginationStyles.navButton}
            disabled={loading || page === maxPage}
            onClick={(): void => updateTable({ ...variables, page: maxPage || 1 })}
          >
            <FontAwesomeIcon icon={faAngleDoubleRight} />
          </button>
        </div>
        <p>
          {resultCount} Result{resultCount === 1 ? '' : 's'}
        </p>
      </div>
    </div>
  );
};

export default RecentTrades;
